import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Modal,
} from "@mui/material";
import AccordionSection from "../../shared/accordionSection";
import dataService from "@/app/services/dataService";
import { NotificationTypeEnum } from "@/enums/notificationTypeEnum";
import { useMutation } from "@tanstack/react-query";
import { useGlobalNotification } from "../../header/GlobalNotificationContext";
import CartToProductListSidebar from "@/app/pages/cart/cartToProductListSidebar";
import ProductListEnumType from "@/enums/productListEnumType";
import { useState } from "react";
import ItemSourceEnum from "@/enums/itemSourceEnum";
import RequestModalConfig from "@/app/interfaces/RequestModalConfig";
import RequestModalData from "@/app/interfaces/RequestModalData";
import { LoadingStatusEnum } from "@/enums/loadingStatusEnum";
import { NoteLocationEnum } from "@/enums/noteLocationEnum";
import RequestModal from "../../modal/RequestModal";
import useAppUrl from "@/app/hooks/useAppUrl";
import { ERROR_MESSAGE } from "@/app/constants/constants";
import {
  OrderDetailsResponse,
} from "@/app/interfaces/response/OrderDetailsResponse";
import ModalHelper from "@/app/helpers/ModalHelper";
import { ModalTypeEnum } from "@/enums/modalTypeEnum";
import { useNavigate } from "react-router-dom";
import CommonHelper from "@/app/helpers/CommonHelper";
import { isEagleViewProduct } from "@/app/models/constants";
import React from "react";
import PageLoadingOverlay from "../../shared/pageLoadingOverlay";

const TITLE = "Order Summary";
const CHANGE_REQUEST_MESSAGE =
  "Thank you for reaching out. A team member will reach out and assist you with this change.";
const MODAL_MESSAGE =
  "We have added the eligible items for Re-Order to your cart. Please note that this may not include any custom items or items that are no longer stocked. Ensure that the Unit of Measure, Quantity, and Color (where applicable) are all correct prior to checkout. Please contact the branch if you have any further questions.";

const OrderSummary = ({ order }: { order: OrderDetailsResponse }) => {
  const { setNotification } = useGlobalNotification();
  const { getAppUrl } = useAppUrl();
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState<boolean>(false);
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatusEnum>(
    LoadingStatusEnum.IDLE
  );
  const [requestModalConfig, setRequestModalConfig] =
    useState<RequestModalConfig | null>(null);

  const openRequestModal = (config: RequestModalConfig) => {
    setRequestModalConfig(config);
    setIsRequestModalOpen(true);
  };

  const { mutateAsync: sendOrderChangeRequest } = useMutation({
    mutationFn: (data: RequestModalData) => {
      const payload = {
        ...data,
        noteLocation: NoteLocationEnum.ORDER_DETAILS,
      };
      return dataService.sendOrderChangeRequest(payload, order.id);
    },
    onMutate: () => {
      setLoadingStatus(LoadingStatusEnum.LOADING);
    },
    onSettled: () => {
      setIsRequestModalOpen(false);
      setRequestModalConfig(null);
      setLoadingStatus(LoadingStatusEnum.IDLE);
    },
    onSuccess: () => {
      setNotification(CHANGE_REQUEST_MESSAGE, NotificationTypeEnum.SUCCESS);
    },
    onError: () => {
      setNotification(ERROR_MESSAGE, NotificationTypeEnum.ERROR);
    },
  });

  const handleSendRequest = async (data: RequestModalData) => {
    await sendOrderChangeRequest(data);
  };

  const { mutate: reorderMutate, isPending } = useMutation({
    mutationFn: () => dataService.reorder(order.id),
    onSuccess: (data) => {
      if (data.success) {
        setIsModalOpen(true);
        window.dispatchEvent(new Event("refresh-cart-silent"));
      } else {
        setNotification(ERROR_MESSAGE, NotificationTypeEnum.ERROR);
      }
    },
    onError: () => {
      setNotification(ERROR_MESSAGE, NotificationTypeEnum.ERROR);
    },
  });

  const addOrderToTemplate = useMutation({
    mutationFn: async ({
      productListId,
      type,
    }: {
      productListId: string;
      type?: ProductListEnumType;
    }) => {
      if (type === ProductListEnumType.QuoteList) {
        return await dataService.AddProductToEstimate({
          estimateId: productListId,
          source: ItemSourceEnum.Order,
          orderId: order.id,
        });
      }

      return await dataService.AddProductToTemplates({
        templateId: productListId,
        source: ItemSourceEnum.Order,
        orderId: order.id,
      });
    },

    onSuccess: (response, { type }) => {
      const { success, data } = response || {};
      const {
        warnings = [],
        id,
        productListName: name,
        newProductListId,
        newProductListName,
        successMessage,
      } = data || {};

      if (success && warnings.length === 0) {
        const listTypeText =
          type === ProductListEnumType.QuoteList
            ? "Estimate List"
            : "Product Template";

        const url = `${process.env.REACT_APP_WEB_URL}/ProductListItem/List/${id}`;

        if (type === ProductListEnumType.BranchList) {
          const newUrl = `${process.env.REACT_APP_WEB_URL}/ProductListItem/List/${newProductListId}`;

          setNotification(
            <>
              Successfully added the product to the {listTypeText}{" "}
              <a href={newUrl}>{newProductListName}</a>.{" "}
              <a href={url}>{name}</a> has been archived
            </>,
            "success"
          );
          return;
        }

        setNotification(
          <>
            {successMessage.includes("{0}") ? (
              successMessage.split("{0}").map((part, index, arr) => (
                <React.Fragment key={index}>
                  {part}
                  {index < arr.length - 1 && <a href={url}>{name}</a>}
                </React.Fragment>
              ))
            ) : (
              <>
                {successMessage} <a href={url}>{name}</a>
              </>
            )}
          </>,
          "success"
        );
      } else {
        setNotification(warnings[0] ?? "Failed to add product.", "error");
      }
    },

    onError: () => {
      setNotification(
        "An unexpected error occurred while adding the product.",
        "error"
      );
    },

    onSettled: () => {
      setSidebarOpen(false);
    },
  });

  const addOrderToNewTemplate = useMutation({
    mutationFn: async ({
      name,
      description,
      type,
    }: {
      name?: string;
      description?: string;
      type?: ProductListEnumType;
    }) => {
      if (!type || !name) throw new Error("Missing name or type");

      return await dataService.createNewEstimateOrTemplate(
        type,
        name.trim(),
        (description ?? "").trim()
      );
    },

    onSuccess: async (response, { type, name }) => {
      if (
        response.success &&
        (!response.data.errorMessage ||
          response.data.errorMessage.trim() === "")
      ) {
        await addOrderToTemplate.mutateAsync({
          productListId: response.data.id?.toString() ?? "0",
          type,
        });
      } else if (
        response.data.errorMessage &&
        response.data.errorMessage.trim() !== ""
      ) {
        setNotification(response.data.errorMessage, "error");
      }
    },

    onError: () => {
      setNotification(
        "An unexpected error occurred while adding the product.",
        "error"
      );
    },

    onSettled: () => {
      setSidebarOpen(false);
    },
  });

  const orderHasEagleView = order.items.some((item) => isEagleViewProduct(item.sku));

  const handleReorder = () => {
    reorderMutate();
  };

  const handleRequestOrderChange = () => {
    openRequestModal(
      ModalHelper.getRequestModalOrderDetailsConfig(
        order,
        ModalTypeEnum.OrderChange
      )
    );
  };

  const handleRequestStatusUpdate = () => {
    openRequestModal(
      ModalHelper.getRequestModalOrderDetailsConfig(
        order,
        ModalTypeEnum.StatusUpdate
      )
    );
  };

  const handleRequestInvoiceChange = () => {
    openRequestModal(
      ModalHelper.getRequestModalOrderDetailsConfig(
        order,
        ModalTypeEnum.InvoiceChange
      )
    );
  };

  const handleRequestQuoteChange = () => {
    openRequestModal(
      ModalHelper.getRequestModalOrderDetailsConfig(
        order,
        ModalTypeEnum.QuoteChange
      )
    );
  };

  const Row = ({
    label,
    value,
    strong,
    dividerTop,
    valueColor,
    fontSize,
  }: {
    label: React.ReactNode;
    value: string;
    strong?: boolean;
    dividerTop?: boolean;
    valueColor?: string;
    fontSize?: number;
  }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        mb: 1,
        ...(dividerTop && { borderTop: "1px solid #ddd", pt: 2, mt: 2 }),
      }}
    >
      <Typography fontSize={fontSize}>{label}</Typography>
      <Typography
        fontWeight={strong ? 700 : 600}
        fontSize={fontSize}
        color={valueColor ? valueColor : strong ? "error.main" : "inherit"}
      >
        {value}
      </Typography>
    </Box>
  );

  const renderContent = () => (
    <Box
      mt={1.5}
      p={isMobile ? 1 : 3}
      borderRadius={2}
      boxShadow={{
        xs: "0 0px 0px rgba(0,0,0,0)",
        md: "0 1px 3px rgba(0,0,0,0.1)",
      }}
      bgcolor="white"
    >
      {!isMobile && (
        <Typography
          variant="subtitle1"
          fontWeight={600}
          color="text.secondary"
          mb={2}
        >
          {TITLE}
        </Typography>
      )}
      <Row
        label={`${order.items.length} ${
          order.items.length === 1 ? "Item" : "Items"
        }`}
        value={CommonHelper.addDolarSign(order.itemsTotal)}
      />
      {order.orderTotalDiscountSKUs && (
        <Row
          label={`Promo Savings [${order.orderTotalDiscountSKUs}]`}
          value={CommonHelper.addDolarSign(order.orderItemsDiscount)}
        />
      )}
      <Row
        label="Credits / IMPACT Rewards:"
        value={
          order.credits === 0 ? "" : CommonHelper.addDolarSign(order.credits)
        }
      />
      <Row
        label="Subtotal:"
        value={CommonHelper.addDolarSign(order.orderSubtotal)}
      />
      {order.orderDiscount !== 0 && (
        <Row
          label="Order Discount:"
          value={CommonHelper.addDolarSign(order.orderDiscount)}
        />
      )}
      {order.charges.map((charge) => (
        <Row
          key={charge.key}
          label={`${charge.key}:`}
          value={CommonHelper.addDolarSign(charge.value)}
        />
      ))}
      <Row label="Taxes:" value={CommonHelper.addDolarSign(order.taxes)} />

      <Row
        label={
          <>
            Order Total:<span style={{ color: "#af272f" }}> *</span>
          </>
        }
        value={CommonHelper.addDolarSign(order.orderTotal)}
        strong
        fontSize={18}
        valueColor="#af272f"
      />
      {order.orderStatus === "Quoted" && order.inforOrderType === "Quote" && (
        <Typography textAlign="end" variant="subtitle2">
          Quoted prices are valid for 30 days
        </Typography>
      )}
      {order.orderStatus === "Ordered" && (
        <Typography
          variant="body2"
          sx={{ color: "#af272f", mt: 1.5, fontSize: "0.85rem" }}
        >
          * Please note, any cost adjustments will be reflected on the final
          invoice.
        </Typography>
      )}
      <Box mt={2} display="flex" flexDirection="column" gap={1}>
        {order.orderStatus === "Quoted" && (
          <>
            <Button
              variant="outlined"
              fullWidth
              href={getAppUrl(`/onepagecheckout?quoteId=${order.id}`)}
            >
              ORDER NOW
            </Button>

            {order.orderDetailsActions.canShowEditQuoteButton && (
              <Button
                variant="outlined"
                fullWidth
                href={getAppUrl(`/Order/EditQuote?quoteId=${order.id}`)}
              >
                EDIT QUOTE
              </Button>
            )}
          </>
        )}
        {!orderHasEagleView &&
          order.orderDetailsActions.canShowReOrderButton &&
          order.orderStatus !== "Quoted" && (
            <Button
              variant="outlined"
              fullWidth
              onClick={handleReorder}
              disabled={
                !order.orderDetailsActions.isReOrderButtonEnabled || isPending
              }
            >
              {isPending ? "RE-ORDERING" : "RE-ORDER"}
            </Button>
          )}
        {!orderHasEagleView && order.orderDetailsActions.canShowAddToTemplateButton && (
          <Button
            variant="outlined"
            fullWidth
            onClick={() => setSidebarOpen(true)}
            disabled={!order.orderDetailsActions.isAddToTemplateButtonEnabled}
          >
            ADD TO TEMPLATE OR ESTIMATE
          </Button>
        )}
        {order.orderDetailsActions.requestChangeButtonType ===
          "OrderChange" && (
          <Button
            variant="outlined"
            fullWidth
            onClick={handleRequestOrderChange}
          >
            REQUEST ORDER CHANGE
          </Button>
        )}
        {order.orderDetailsActions.requestChangeButtonType ===
          "InvoiceChange" && (
          <Button
            variant="outlined"
            fullWidth
            onClick={handleRequestInvoiceChange}
          >
            REQUEST INVOICE CHANGE
          </Button>
        )}
        {order.orderDetailsActions.requestChangeButtonType ===
          "QuoteChange" && (
          <Button
            variant="outlined"
            fullWidth
            onClick={handleRequestQuoteChange}
          >
            REQUEST QUOTE CHANGE
          </Button>
        )}
        {order.orderDetailsActions.canShowRequestStatusUpdateButton && (
          <Button
            variant="outlined"
            fullWidth
            onClick={handleRequestStatusUpdate}
            disabled={
              !order.orderDetailsActions.isRequestStatusUpdateButtonEnabled
            }
          >
            REQUEST STATUS UPDATE
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <AccordionSection title={TITLE}>{renderContent()}</AccordionSection>
      ) : (
        renderContent()
      )}
      <CartToProductListSidebar
        open={sidebarOpen}
        onCartToNewTemplate={(
          name?: string,
          description?: string,
          type?: ProductListEnumType
        ) => {
          if (name && type) {
            addOrderToNewTemplate.mutate({ name, description, type });
          }
        }}
        onCartToTemplate={(id?: string, type?: ProductListEnumType) => {
          if (id && type) {
            addOrderToTemplate.mutate({ productListId: id, type });
          }
        }}
        onClose={() => {
          setSidebarOpen(false);
        }}
      />
      {isRequestModalOpen && requestModalConfig && (
        <RequestModal
          open={isRequestModalOpen}
          loadingStatus={loadingStatus}
          onClose={() => {
            setIsRequestModalOpen(false);
            setRequestModalConfig(null);
          }}
          onSend={handleSendRequest}
          {...requestModalConfig}
        />
      )}
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        sx={{ zIndex: 10000 }}
      >
        <Box
          sx={{
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            maxWidth: "90%",
            backgroundColor: "background.paper",
            boxShadow: 24,
            p: 4,
            textAlign: "center",
            borderRadius: "8px",
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            SUCCESS
          </Typography>
          <Typography sx={{ mt: 2, mb: 3 }}>{MODAL_MESSAGE}</Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setIsModalOpen(false)}
            sx={{ marginRight: 2 }}
          >
            OK
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate("/cart")}
          >
            Go To Cart
          </Button>
        </Box>
      </Modal>
      {(addOrderToTemplate.isPending || addOrderToNewTemplate.isPending) && (
        <PageLoadingOverlay />
      )}
    </>
  );
};

export default OrderSummary;
